import { useQueries, useQuery } from '@tanstack/react-query';
import { TZDate } from '@date-fns/tz';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePythHermesPriceAt } from '@ui/services/pyth-hermes/price-at-time/usePythHermesPriceAt';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { PairResponsePairTypeEnum } from '@ui/services/backend/generated/models';
import type { Address } from '@ui/types/alias';
import { parseSchedule } from '@ui/utils/parseSchedule';
import BigNumber from 'bignumber.js';
import { startOfHour } from 'date-fns';
import { useMemo, useState } from 'react';

import { oraclePriceControllerApi } from '../../clients/clients';
import type {
  GetPair24hAgoPriceRequest,
  GetPair24hAgoPriceBlockChainEnum,
} from '../../generated/apis/OraclePriceControllerApi';
import type { PairHoursAgoPriceResponse } from '../../generated/models';
import { ResponseError } from '../../generated/runtime';
import { toServerBlockChain } from '../../utils';
import { formatEther, parseEther } from 'viem';

type UsePair24hAgoPriceParams = {
  pairBase?: Address;
  pythId?: string;
  currentPrice?: number;
  enabled?: boolean;
};

const CACHE_5_MINUTES = 5 * 60 * 1000;
const BACKUP_24H_SECONDS = 24 * 60 * 60;
const DAY_SECONDS = 24 * 60 * 60;
const MINUTE_SECONDS = 60;
const MINUTES_PER_DAY = 24 * 60;
const MAX_LOOKBACK_DAYS = 14;
type HermesPricePoint = { id: string; price: BigNumber };
type HermesTypeGroup = {
  type: PairResponsePairTypeEnum;
  pythIds: string[];
  schedule?: string;
};

const isResponse404 = (error: unknown) => {
  if (error instanceof ResponseError) {
    return error.response.status === 404;
  }

  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'ResponseError' &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    error.response.status === 404
  );
};

const hhmmToMinutes = (value: string) => {
  const hour = Number(value.slice(0, 2));
  const minute = Number(value.slice(2, 4));

  return hour * MINUTE_SECONDS + minute;
};

const getHolidayScheduleByDate = ({
  holidays,
  month,
  day,
}: {
  holidays: string[];
  month: number;
  day: number;
}) => {
  const mmdd = `${String(month + 1).padStart(2, '0')}${String(day).padStart(
    2,
    '0',
  )}`;
  const holiday = holidays.find((item) => item.startsWith(`${mmdd}/`));
  if (!holiday) return;

  return holiday.split('/')[1];
};

const getSessionRanges = (session?: string) => {
  if (!session || session === 'C') return [];
  if (session === 'O') {
    return [{ startMinute: 0, endMinute: MINUTES_PER_DAY }];
  }

  return session
    .split('&')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [start, end] = part.split('-');
      if (!start || !end) return null;

      return {
        startMinute: hhmmToMinutes(start),
        endMinute: hhmmToMinutes(end),
      };
    })
    .filter((range): range is { startMinute: number; endMinute: number } => {
      if (!range) return false;
      if (range.endMinute === 0) {
        return range.startMinute < MINUTES_PER_DAY;
      }

      return range.startMinute < range.endMinute;
    });
};

const getPreviousSessionOpenTimestampSecond = ({
  schedule,
  targetTimestampSecond,
}: {
  schedule?: string;
  targetTimestampSecond: number;
}) => {
  if (!schedule) return;
  const { timezone, weekSchedule, holidays } = parseSchedule(schedule);
  const targetMs = targetTimestampSecond * 1000;
  const targetTzDate = new TZDate(targetMs).withTimeZone(timezone);
  const targetMinute =
    targetTzDate.getHours() * MINUTE_SECONDS + targetTzDate.getMinutes();

  for (let dayOffset = 0; dayOffset <= MAX_LOOKBACK_DAYS; dayOffset += 1) {
    const dayMs = targetMs - dayOffset * DAY_SECONDS * 1000;
    const dayTzDate = new TZDate(dayMs).withTimeZone(timezone);
    const month = dayTzDate.getMonth();
    const day = dayTzDate.getDate();
    const weekdayMondayFirst = (dayTzDate.getDay() + 6) % 7;
    const holidaySession = getHolidayScheduleByDate({ holidays, month, day });
    const sessionForDay = holidaySession ?? weekSchedule[weekdayMondayFirst];
    const sessionRanges = getSessionRanges(sessionForDay);
    if (sessionRanges.length === 0) continue;

    if (dayOffset === 0) {
      const inSession = sessionRanges.find(
        (session) =>
          targetMinute >= session.startMinute && targetMinute < session.endMinute,
      );
      if (inSession) {
        const hour = Math.floor(inSession.startMinute / MINUTE_SECONDS);
        const minute = inSession.startMinute % MINUTE_SECONDS;

        return Math.floor(
          new TZDate(
            dayTzDate.getFullYear(),
            month,
            day,
            hour,
            minute,
            timezone,
          ).getTime() / 1000,
        );
      }

      const previousSession = [...sessionRanges]
        .filter((session) => session.startMinute < targetMinute)
        .sort((a, b) => b.startMinute - a.startMinute)[0];
      if (!previousSession) continue;
      const hour = Math.floor(previousSession.startMinute / MINUTE_SECONDS);
      const minute = previousSession.startMinute % MINUTE_SECONDS;

      return Math.floor(
        new TZDate(
          dayTzDate.getFullYear(),
          month,
          day,
          hour,
          minute,
          timezone,
        ).getTime() / 1000,
      );
    }

    const latestSession = [...sessionRanges].sort(
      (a, b) => b.startMinute - a.startMinute,
    )[0];
    const hour = Math.floor(latestSession.startMinute / MINUTE_SECONDS);
    const minute = latestSession.startMinute % MINUTE_SECONDS;

    return Math.floor(
      new TZDate(
        dayTzDate.getFullYear(),
        month,
        day,
        hour,
        minute,
        timezone,
      ).getTime() / 1000,
    );
  }
};

export const usePair24hAgoPrice = ({
  pairBase,
  pythId,
  currentPrice,
  enabled = true,
}: UsePair24hAgoPriceParams) => {
  const chainId = useDataChainId();
  const { data: allPairs } = useBackendPairsAll();
  const [{ from }] = useState(() => {
    const now = startOfHour(Math.floor(Date.now())).getTime() / 1000;

    return { from: now - BACKUP_24H_SECONDS };
  });
  const hermesTypeGroups = useMemo(() => {
    const byType: Record<PairResponsePairTypeEnum, HermesTypeGroup> = {
      [PairResponsePairTypeEnum.Crypto]: {
        type: PairResponsePairTypeEnum.Crypto,
        pythIds: [],
      },
      [PairResponsePairTypeEnum.Stocks]: {
        type: PairResponsePairTypeEnum.Stocks,
        pythIds: [],
      },
      [PairResponsePairTypeEnum.Forex]: {
        type: PairResponsePairTypeEnum.Forex,
        pythIds: [],
      },
      [PairResponsePairTypeEnum.Indices]: {
        type: PairResponsePairTypeEnum.Indices,
        pythIds: [],
      },
      [PairResponsePairTypeEnum.Commodities]: {
        type: PairResponsePairTypeEnum.Commodities,
        pythIds: [],
      },
    };
    Object.values(allPairs ?? {}).forEach((pair) => {
      if (!pair.pythPriceFeedId) return;
      byType[pair.type].pythIds.push(pair.pythPriceFeedId);
      if (!byType[pair.type].schedule && pair.schedule) {
        byType[pair.type].schedule = pair.schedule;
      }
    });
    (
      Object.keys(byType) as PairResponsePairTypeEnum[]
    ).forEach((type) => {
      byType[type].pythIds = Array.from(new Set(byType[type].pythIds)).sort();
    });

    return Object.values(byType).filter((group) => group.pythIds.length > 0);
  }, [allPairs]);

  const query = useQuery({
    queryKey: ['oraclePriceControllerApi.getPair24hAgoPrice', chainId, pairBase],
    enabled: !!pairBase && enabled,
    staleTime: CACHE_5_MINUTES,
    gcTime: CACHE_5_MINUTES,
    retry: (failureCount, error) => !isResponse404(error) && failureCount < 3,
    queryFn: async (): Promise<PairHoursAgoPriceResponse> => {
      if (!pairBase) {
        throw new Error('pairBase is missing');
      }

      const request: GetPair24hAgoPriceRequest = {
        pairBase,
        blockChain: toServerBlockChain<GetPair24hAgoPriceBlockChainEnum>(chainId),
      };

      return oraclePriceControllerApi.getPair24hAgoPrice(request);
    },
  });
  const shouldUseBackup = isResponse404(query.error);

  const hermesTypeQueries = useMemo(() => {
    return hermesTypeGroups.map((group) => {
      if (
        group.type === PairResponsePairTypeEnum.Stocks ||
        group.type === PairResponsePairTypeEnum.Indices ||
        group.type === PairResponsePairTypeEnum.Commodities
      ) {
        return {
          ...group,
          timestampSecond:
            getPreviousSessionOpenTimestampSecond({
              schedule: group.schedule,
              targetTimestampSecond: from,
            }) ?? from,
        };
      }

      return {
        ...group,
        timestampSecond: from,
      };
    });
  }, [from, hermesTypeGroups]);

  const backupPrices24HrAgo = useQueries({
    queries: hermesTypeQueries.map(({ type, timestampSecond, pythIds }) => {
      return {
        queryKey: [
          'pyth-hermes-updates-price-by-type',
          { type, pythIds, timestampSecond },
        ],
        queryFn: usePythHermesPriceAt.getFn(pythIds, timestampSecond),
        enabled: shouldUseBackup && enabled && pythIds.length > 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      };
    }),
    combine(results) {
      const mergedMap = new Map<string, BigNumber>();
      results.forEach((result) => {
        const rows = (result.data ?? []) as HermesPricePoint[];
        rows.forEach((row) => {
          if (!mergedMap.has(row.id)) {
            mergedMap.set(row.id, row.price);
          }
        });
      });

      return Array.from(mergedMap.entries()).map(([id, price]) => ({
        id,
        price,
      })) as HermesPricePoint[];
    },
  });

  if (currentPrice === undefined) {
    return undefined;
  }

  const backendPrice24hAgo = formatEther(BigInt(query.data?.pricePoint?.price ?? '0'))
  const backupPrice24hAgo = backupPrices24HrAgo?.find(
    (price: HermesPricePoint) => price.id === pythId,
  )?.price;
  const price24hAgoRaw = backendPrice24hAgo ?? backupPrice24hAgo;

  if (!price24hAgoRaw) {
    return undefined;
  }

  const price24hAgoBN = new BigNumber(price24hAgoRaw);
  if (!price24hAgoBN.isFinite() || price24hAgoBN.isZero()) {
    return undefined;
  }

  const change = new BigNumber(currentPrice).minus(price24hAgoBN);
  const rate = change.div(price24hAgoBN);

  return {
    ...query,
    change,
    rate,
    price24hAgo: price24hAgoBN,
  };
};
